import { Injectable } from '@angular/core';
import {
    arrayFind,
    distinctUntilArrayItemChanged,
    filterNilValue,
    Order,
    QueryEntity
} from '@datorama/akita';
import { InstrumentsState, InstrumentsStore } from './instruments.store';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { Instrument, InstrumentType } from './instrument.model';
import {
    checkFilter,
    flattenNestedObject,
    InferFilter,
    QueryFilterNew
} from '../query-filter.functions';
import { get } from 'dot-prop';
import { Observable } from 'rxjs';
import { JournalEntry } from '../../../features/accounting/journal-entries/state/journal-entry.model';
import { AccountTypeEnum } from '../accts/acct.model';

@Injectable({ providedIn: 'root' })
export class InstrumentsQuery extends QueryEntity<InstrumentsState> {
    selectInstrumentsByFilter$<T = any>(
        filters: InferFilter<Instrument<T>> = {},
        sortBy: keyof Instrument | any = 'decoratedInstrumentName',
        sortByOrder: Order = Order.ASC
    ) {
        return this.selectAll({
            sortBy,
            sortByOrder,
            filterBy: (instrument) => {
                // ...flatten nested object and filter out null values
                const FILTERS = Object.entries<any>(flattenNestedObject(filters)).filter(
                    (filter) => filter[1] !== null
                );
                if (FILTERS.length > 0) {
                    return !FILTERS.find(([key, criteria]) => {
                        const value = get<string | number>(instrument, key);
                        return !checkFilter(value, criteria);
                    });
                } else {
                    return true;
                }
            }
        });
    }

    getById(id: Instrument['instrumentId']) {
        return this.getEntity(id);
    }

    isLoading$ = this.selectLoading();
    all$ = this.selectAll().pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) !== JSON.stringify(b))
        //distinctUntilArrayItemChanged()
    );
    companies$ = this.selectAll({
        filterBy: (instrument) => instrument.type === 'COMPANY'
    });

    allAsObject$ = this.selectAll({ asObject: true }).pipe(shareReplay(1));

    getInstrumentsByType = (type: InstrumentType) =>
        this.selectAll({ filterBy: (instrument) => instrument.type === type });

    selectInstrumentById$ = (id) => this.selectEntity(id).pipe(filterNilValue());

    selectInstrumentByName$(name: string) {
        return this.selectAll({
            filterBy: (instrument) =>
                instrument.name?.toLowerCase().includes(name?.toLowerCase())
        }).pipe(
            map((instruments) => instruments[0] || null),
            distinctUntilChanged((a, b) => a?.instrumentId === b?.instrumentId),
            shareReplay(1)
        );
    }

    getInstrumentById = (id: number) => this.getEntity(id);

    selectInstrumentsByDecoratedName$ = (name: string) =>
        this.selectAll({
            filterBy: (instrument) =>
                instrument.decoratedInstrumentName
                    ?.toLowerCase()
                    .includes(name.toLowerCase())
        });

    getSubInstrumentsByIdForType = (id, type?) =>
        this.selectAll({
            filterBy: (instrument) =>
                (type ? instrument.type === type : true) &&
                instrument.underlyingInstrumentId === id
        });

    getValuationsByInstrumentId$ = (instrumentId: number) =>
        this.selectEntity(instrumentId, 'valuations').pipe(
            map((valuations) => valuations || [])
        );

    getValuationByInstrumentIdForId$ = (instrumentId: number, id: string) =>
        this.selectEntity(instrumentId, 'valuations').pipe(arrayFind(id));

    selectAvailableInstrumentTypes$<T = any>(
        filters: InferFilter<Instrument<T>> = {
            type: { nin: ['COMPANY', 'CASH'] }
        }
    ) {
        return this.selectInstrumentsByFilter$<T>(filters).pipe(
            map((instruments) => {
                return Array.from(
                    new Set(instruments.map((instrument) => instrument.type))
                );
            }),
            distinctUntilArrayItemChanged(),
            shareReplay(1)
        );
    }

    constructor(protected store: InstrumentsStore) {
        super(store);
    }
}
